/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

SQC-562: Added MidFirst Transfer nav link — teman.beck
*/
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import NavBarStyles from '../css/LeftNavBar.module.css';
import MyLoan from '../assets/my-loan.png';
import LoanInfo from '../assets/loan-information.png';
import Requests from '../assets/loan-requests.png';
import LoanTools from '../assets/loan-tools.png';
import LaunchIcon from '@mui/icons-material/Launch';
import { useCallback, useMemo, useRef } from 'react';
// import { getStorageKey } from '../common/UtilityFunctions';
import { useLoanContext } from '../contexts/LoanContext';
import { useLoanInformationContext } from '../contexts/LoanInformationContext';
import useFocusIn from '../common/hooks/useFocusIn';
import { useRootContext } from '../contexts/RootContext';
import { useViewingContext } from '../contexts/ViewingContext';
import { useBorrowerEscrowAnalysisContext } from '../contexts/BorrowerEscrowAnalysisContext';
import { useAuthContext } from '../contexts/AuthContext';
import useGetCompany from '../common/hooks/useGetCompany';
import useGetComment from '../common/hooks/useGetComment';
import useGetUserData from '../common/hooks/useGetUserData';
import useGetUserDutyCodes from '../common/hooks/useGetUserDutyCodes';
import {
  isAfter9AMPTOnJuly1st2026AndBefore9PMPTOnJuly2nd2026,
  isStrEmptyOrNull,
  lpiBeforePreviousMonth,
  containsCashieringTeamDutyCode,
} from '../common/UtilityFunctions';

const LeftNavBar = () => {
  const { loanNumber, loanHeloc, lpi } = useLoanContext();
  const {
    loading: loadingServicing,
    servicingData,
    loanData,
    lnptData,
    originalLoanData,
  } = useLoanInformationContext();
  const { closeNavIfMobile } = useRootContext();
  const { pathname } = useLocation();
  const { isServicerView } = useViewingContext();
  const { sortedAEADocs } = useBorrowerEscrowAnalysisContext();

  const { userId, servicerUser, superUser, company, fullAccessGNMA, hasProctorReportAccess } = useAuthContext();
  const { userData, userDataErrors, loadingUserData } = useGetUserData(userId);
  const { userDutyCodes, userDutyCodesErrors, loadingUserDutyCodes } = useGetUserDutyCodes(userId, company);

  const { companyDetails: companyResp, loadingCompanyDetails: loadingComp } = useGetCompany('GNMA00000');
  const { comment: commentResp, loadingComment: loadingComment } = useGetComment(
    companyResp && companyResp.comment ? companyResp.comment : '',
  );

  const [lpiBeforePrevMonth, setLpiBeforePrevMonth] = useState(false);
  const [cashieringTeam, setCashieringTeam] = useState(false);

  const showLinks = useMemo(() => {
    if (loadingComp || loadingComment || loadingServicing) return false;
    if (
      !loadingComp &&
      !loadingComment &&
      servicingData &&
      ['331', '332', '333'].includes(servicingData.investorCode) &&
      commentResp &&
      companyResp &&
      !isStrEmptyOrNull(commentResp.comment) &&
      isAfter9AMPTOnJuly1st2026AndBefore9PMPTOnJuly2nd2026() &&
      !(
        servicerUser &&
        [
          '7SK8',
          '5AD8',
          '4TB4',
          '4ME4',
          'AP4E',
          'L8VZ',
          '8SS6',
          '5LS5',
          '4RNA',
          '5RP6',
          '4US4',
          'S8MC',
          'NJ4B',
          'IGNR',
          '4SM1',
          '4AA6',
          'RP8Z',
        ].includes(userId)
      )
    ) {
      return false;
    }
    return true;
  }, [loadingComp, loadingComment, servicingData, commentResp, companyResp, servicerUser, userId, loadingServicing]);

  useEffect(() => {
    if (lpi) {
      setLpiBeforePrevMonth(lpiBeforePreviousMonth(lpi));
    }
  }, [lpi]);

  useEffect(() => {
    if (userDataErrors === '' || !loadingUserData || userDutyCodesErrors === '' || !loadingUserDutyCodes) {
      setCashieringTeam(containsCashieringTeamDutyCode(userData?.defaultDuty, userDutyCodes));
    }
  }, [userData, userDutyCodes]);

  // Keep track of the Details sections.
  const detailsRef = useRef({});
  const linksRef = useRef(null);
  // Keep track of skip links for keyboard navigation
  const skipNavigationRef = useRef(null);
  const skipSetupCategoryLinksRef = useRef(null);
  const skipLoanInformationCategoryLinksRef = useRef(null);
  const skipLoanActionsCategoryLinksRef = useRef(null);
  const skipLoanToolsCategoryLinksRef = useRef(null);

  /**
   * Closes all other Details sections when one is opened.
   *
   * @param {Event} e - The event object from the click event.
   *
   * This function checks if the current Details section is open. If so, it:
   * 1) iterates through the details and closes any other section that is still open.
   * 2) if the Section dropdown is not visible, scrolls into view of the Section dropdown.
   *
   * Note: theoretically 1) is possible by just setting name="section" on each
   * Details element to group them, but I was unable to replicate that behavior.
   */
  const handleToggle = useCallback(async (e) => {
    e?.preventDefault();
    if (e.target.open) {
      Object.keys(detailsRef.current).forEach((key) => {
        if (e.target !== detailsRef.current[key] && detailsRef.current[key].open) detailsRef.current[key].open = false;
      });
      const rect = e.target.getBoundingClientRect();
      const isVisible =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth);
      if (!isVisible) e.target.scrollIntoView();
    }
  }, []);

  /**
   * Opens a URL in a new tab with a specific query parameter added.
   *
   * @param {Event} e - The event object from the click event.
   *
   * This function prevents the default action of the event, constructs a URL from the current
   * location and the href attribute of the clicked element, appends a 'key' query parameter to it,
   * and then opens the constructed URL in a new browser tab.
   */

  // const openInNewTab = useCallback(async (e) => {
  //   // Prevent the default action of the event (e.g., following a link)
  //   e?.preventDefault();

  //   // Get the storage key asynchronously (presumably from some storage or API)
  //   const key = await getStorageKey(authContext);

  //   // Construct the base URL from the current location (protocol + domain)
  //   const base = window.location.href.split('/').slice(0, 3).join('/');

  //   // Create a new URL object based on the base URL and the href attribute of the clicked element
  //   const url = new URL(base + e.target.getAttribute('href'));

  //   // Add the 'key' parameter to the URL's query parameters
  //   url.searchParams.set('key', key);

  //   // Open the constructed URL in a new browser tab
  //   window.open(`${url}`, '_blank');
  // }, [authContext]);

  const loanInformationCategoryLinks = useMemo(() => {
    const links = [
      '/loan-information',
      '/loan-history',
      '/loan-buydown',
      '/escrow-payment',
      '/payment-history',
      '/loan-escrow-accruals',
      '/escrow-analysis',
      '/documents',
      '/loan-payments-due',
      '/communication',
      '/loan-arm',
      '/amortization-schedule',
      '/loan-subsidy',
      '/loss-draft-repair',
    ];
    return links.includes(pathname);
  }, [pathname]);

  const loanActionsCategoryLinks = useMemo(() => {
    const links = [
      '/make-one-payment',
      '/recurring-payment',
      '/go-green',
      '/change-contact-info',
      '/pull-payoff',
      '/loan-default-codes',
    ];
    return links.includes(pathname);
  }, [pathname]);

  const loanToolsCategoryLinks = useMemo(() => {
    const links = [
      '/add-stop-code',
      '/add-edit-payee',
      '/bulk-disbursement',
      '/upload-payees',
      '/payments-and-disbursements',
      '/pmi-servicing',
      '/mip-servicing',
      '/midfirst-transfer',
      '/tax-payment',
      '/glossary',
      '/contact-us',
      '/privacy-policy',
      '/disclaimers',
      '/faq',
    ];
    return Boolean(links.find((path) => pathname.startsWith(path)));
  }, [pathname]);

  const handleClick = useCallback((e) => {
    const target = e.target.getAttribute('data-to');
    let ref = { focus: null };
    switch (target) {
      case 'skipNavigationRef':
        ref = skipNavigationRef.current;
        break;
      case 'skipSetupCategoryLinksRef':
        ref = skipSetupCategoryLinksRef.current;
        break;
      case 'skipLoanInformationCategoryLinksRef':
        ref = skipLoanInformationCategoryLinksRef.current;
        break;
      case 'skipLoanActionsCategoryLinksRef':
        ref = skipLoanActionsCategoryLinksRef.current;
        break;
      case 'skipLoanToolsCategoryLinksRef':
        ref = skipLoanToolsCategoryLinksRef.current;
        break;
      default:
        ref = skipLoanToolsCategoryLinksRef.current;
        break;
    }
    if (ref && typeof ref === 'object' && typeof ref.focus === 'function') ref.focus();
    return;
  }, []);

  const handleFocusIn = useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    if (target.tagName !== 'A') return;

    const details =
      target.parentElement?.parentElement?.parentElement?.parentElement?.parentElement?.querySelector('details');
    if (!details || typeof details.getAttribute !== 'function') return;
    if (details.open) return;

    const summaryElement = details.firstElementChild as HTMLElement;
    if (summaryElement && typeof summaryElement.click === 'function') {
      summaryElement.click();
    }
  }, []);
  useFocusIn(linksRef, handleFocusIn);

  return (
    <aside className={NavBarStyles.navbarAside}>
      <div>
        <div>
          {/* SWMC Logo */}
          {/* If we add a dark mode in the future, we can use REACT_APP_SWMC_WHITE_LOGO_PATH */}
          <img
            src={process.env.REACT_APP_CDN_LINK + process.env.REACT_APP_SWMC_BLUE_LOGO_PATH}
            alt='Logo'
            className={NavBarStyles.logo}
          />

          {/* Overarching list to contain all categories. If a new category is added, it will be a new <li> child. */}
          <ul ref={linksRef}>
            {/* Setup category*/}
            <li>
              <button
                onClick={handleClick}
                data-to='skipLoanToolsCategoryLinksRef'
                className='keyboard-only'
                ref={skipNavigationRef}
              >
                <div>
                  <div>Skip Navigation</div>
                </div>
              </button>
              <button
                onClick={handleClick}
                data-to={loanNumber ? 'skipSetupCategoryLinksRef' : 'skipLoanActionsCategoryLinksRef'}
                className='keyboard-only'
              >
                <div>
                  <div>Skip Section</div>
                </div>
              </button>
              <NavLink
                onClick={closeNavIfMobile}
                to='/select-loans'
                className={({ isActive }) => (isActive ? NavBarStyles.selectionActive : NavBarStyles.selection)}
              >
                <img src={MyLoan} alt='Logo' className={NavBarStyles.icons} />
                <span className={NavBarStyles.categoryText}>Select Loans</span>
                <span aria-hidden>▼</span>
              </NavLink>
            </li>

            {/* Loan Information category*/}
            {loanNumber && showLinks && (
              <li>
                <button
                  onClick={handleClick}
                  data-to='skipLoanInformationCategoryLinksRef'
                  className='keyboard-only'
                  ref={skipSetupCategoryLinksRef}
                >
                  <div>
                    <div>Skip Section</div>
                  </div>
                </button>
                <details
                  ref={(element) => (detailsRef.current['loanInfo'] = element)}
                  onToggle={handleToggle}
                  open={loanInformationCategoryLinks}
                >
                  <summary>
                    <img src={LoanInfo} alt='Logo' className={NavBarStyles.icons} />
                    <span className={NavBarStyles.categoryText}>Loan Information</span>
                    <span aria-hidden>▼</span>
                  </summary>
                </details>
                <div>
                  <div>
                    <ul>
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/loan-information'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Loan Information
                        </NavLink>
                      </li>
                      {isServicerView ? (
                        <li>
                          <NavLink
                            onClick={closeNavIfMobile}
                            to='/loan-history'
                            className={({ isActive }) =>
                              isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                            }
                          >
                            Loan History
                          </NavLink>
                        </li>
                      ) : (
                        <li>
                          <NavLink
                            onClick={closeNavIfMobile}
                            to='/payment-history'
                            className={({ isActive }) =>
                              isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                            }
                          >
                            Payment History
                          </NavLink>
                        </li>
                      )}
                      {isServicerView && (
                        <li>
                          <NavLink
                            onClick={closeNavIfMobile}
                            to='/communication'
                            className={({ isActive }) =>
                              isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                            }
                          >
                            Communication
                          </NavLink>
                        </li>
                      )}
                      {isServicerView && (
                        <li>
                          <NavLink
                            onClick={closeNavIfMobile}
                            to='/loan-buydown'
                            className={({ isActive }) =>
                              isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                            }
                          >
                            Loan Buydown
                          </NavLink>
                        </li>
                      )}
                      {isServicerView && (
                        <li>
                          <NavLink
                            onClick={closeNavIfMobile}
                            to='/escrow-payment'
                            className={({ isActive }) =>
                              isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                            }
                          >
                            Escrow Payment
                          </NavLink>
                        </li>
                      )}
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/loan-escrow-accruals'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Loan Escrow Accruals
                        </NavLink>
                      </li>
                      {(isServicerView || (!isServicerView && sortedAEADocs?.length != 0)) && (
                        <li>
                          <NavLink
                            onClick={closeNavIfMobile}
                            to='/escrow-analysis'
                            className={({ isActive }) =>
                              isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                            }
                          >
                            Escrow Analysis
                          </NavLink>
                        </li>
                      )}
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/documents'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Documents
                        </NavLink>
                      </li>
                      {/* <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/irs-1040-tax-info'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          IRS 1040 Tax Info
                        </NavLink>
                      </li> */}
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to={`/loan-payments-due?loanNumber=${loanNumber}`}
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Loan Payments Due
                        </NavLink>
                      </li>
                      {isServicerView && (
                        <li>
                          <NavLink
                            onClick={closeNavIfMobile}
                            to={`/loan-arm?loanNumber=${loanNumber}`}
                            className={({ isActive }) =>
                              isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                            }
                          >
                            Loan ARM
                          </NavLink>
                        </li>
                      )}
                      {!loanHeloc && (
                        <li>
                          <NavLink
                            onClick={closeNavIfMobile}
                            to='/amortization-schedule'
                            className={({ isActive }) =>
                              isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                            }
                          >
                            Amortization Schedule
                          </NavLink>
                        </li>
                      )}
                      {isServicerView && (
                        <li>
                          <NavLink
                            onClick={closeNavIfMobile}
                            to='/loan-subsidy'
                            className={({ isActive }) =>
                              isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                            }
                          >
                            Loan Subsidy Page
                          </NavLink>
                        </li>
                      )}
                      {isServicerView && (
                        <li>
                          <NavLink
                            onClick={closeNavIfMobile}
                            to={`/loss-draft-repair?loanNumber=${loanNumber}`}
                            className={({ isActive }) =>
                              isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                            }
                          >
                            Loss Draft Repair
                          </NavLink>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </li>
            )}

            {/* Loan Actions category */}
            {loanNumber && showLinks && (
              <li>
                <button
                  onClick={handleClick}
                  data-to='skipLoanActionsCategoryLinksRef'
                  className='keyboard-only'
                  ref={skipLoanInformationCategoryLinksRef}
                >
                  <div>
                    <div>Skip Section</div>
                  </div>
                </button>
                <details
                  ref={(element) => (detailsRef.current['loanActions'] = element)}
                  onToggle={handleToggle}
                  open={loanActionsCategoryLinks}
                >
                  <summary>
                    <img src={Requests} alt='Logo' className={NavBarStyles.icons} />
                    <span className={NavBarStyles.categoryText}>Loan Actions</span>
                    <span aria-hidden>▼</span>
                  </summary>
                </details>
                <div>
                  <div>
                    <ul>
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/make-one-payment'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          One Time Payments
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/recurring-payment'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Set Up Recurring Payments
                        </NavLink>
                      </li>
                      {
                        <li>
                          <NavLink
                            onClick={closeNavIfMobile}
                            to='/go-green'
                            className={({ isActive }) =>
                              isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                            }
                          >
                            Go Green
                          </NavLink>
                        </li>
                      }
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/change-contact-info'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Change Contact Info
                        </NavLink>
                      </li>
                      {/*Borrower, superUser, Cashiering QC Team, or 
                      (no deffered prinipal balance, no prepayment penalty, no foreclosure status, no bankruptcy status, no ST13 stop code,
                      LPI is not before previous month, property state is not North Carolina, not a heloc loan, no payment supplement or principal suspense) 
                      */}
                      {(!servicerUser ||
                        superUser ||
                        cashieringTeam ||
                        (lnptData?.deferPrincipalBal <= 0 &&
                          !loanData?.prepaymentPenalty &&
                          loanData?.foreclosureMainStatus === '' &&
                          servicingData?.defaultBankruptcyStatus === '' &&
                          originalLoanData?.servicingStopCodes.filter(
                            (stopCode: ServicingStopCode) => stopCode.servStopCode === 'ST13',
                          ).length === 0 &&
                          !lpiBeforePrevMonth &&
                          loanData.propertyState !== 'NC' &&
                          !loanData.loanHeloc &&
                          servicingData.additionalPrincipal <= 0 &&
                          lnptData.suspenseBal <= 0)) && (
                        <li>
                          <NavLink
                            onClick={closeNavIfMobile}
                            to='/pull-payoff'
                            className={({ isActive }) =>
                              isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                            }
                          >
                            Pull Payoff
                          </NavLink>
                        </li>
                      )}
                      {isServicerView && (
                        <li>
                          <NavLink
                            onClick={closeNavIfMobile}
                            to='/loan-default-codes'
                            className={({ isActive }) =>
                              isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                            }
                          >
                            Loan Default Codes
                          </NavLink>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </li>
            )}

            {/* Tools & Help category */}
            <li>
              <button
                onClick={handleClick}
                data-to='skipLoanToolsCategoryLinksRef'
                className='keyboard-only'
                ref={skipLoanActionsCategoryLinksRef}
              >
                <div>
                  <div>Skip Section</div>
                </div>
              </button>
              <details
                ref={(element) => (detailsRef.current['toolsAndHelp'] = element)}
                onToggle={handleToggle}
                open={loanToolsCategoryLinks}
              >
                <summary>
                  <img src={LoanTools} alt='Logo' className={NavBarStyles.icons} />
                  <span className={NavBarStyles.categoryText}>Tools & Help</span>
                  <span aria-hidden>▼</span>
                </summary>
              </details>
              <div>
                <div>
                  <ul>
                    {isServicerView && (
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/add-stop-code'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Add Stop Code
                        </NavLink>
                      </li>
                    )}
                    {isServicerView && (
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/add-edit-payee'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Add Edit Payee
                        </NavLink>
                      </li>
                    )}
                    {isServicerView && (
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/bulk-disbursement'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Bulk Disbursements
                        </NavLink>
                      </li>
                    )}
                    {isServicerView && (
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/upload-payees'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Upload Payees
                        </NavLink>
                      </li>
                    )}
                    {isServicerView && (
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/payments-and-disbursements'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Payments & Disbursements
                        </NavLink>
                      </li>
                    )}
                    {isServicerView && (
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/pmi-servicing'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          PMI Servicing
                        </NavLink>
                      </li>
                    )}
                    {isServicerView && (
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/mip-servicing'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          MIP Servicing
                        </NavLink>
                      </li>
                    )}
                    {isServicerView && (
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/midfirst-transfer'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          MidFirst Transfer
                        </NavLink>
                      </li>
                    )}
                    {isServicerView && (
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/tax-payment'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Tax Payments
                        </NavLink>
                      </li>
                    )}
                    {isServicerView && (
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/borrower-login-audit'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Borrower Login Audit
                        </NavLink>
                      </li>
                    )}
                    {isServicerView && hasProctorReportAccess && (
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/proctor-import-reports'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          Proctor Import Reports
                        </NavLink>
                      </li>
                    )}
                    {isServicerView && fullAccessGNMA && (
                      <li>
                        <NavLink
                          onClick={closeNavIfMobile}
                          to='/gnma-reporting'
                          className={({ isActive }) =>
                            isActive ? NavBarStyles.selectionActive : NavBarStyles.selection
                          }
                        >
                          GNMA Reporting
                        </NavLink>
                      </li>
                    )}
                    <li>
                      <NavLink
                        onClick={closeNavIfMobile}
                        to='/glossary'
                        className={({ isActive }) => (isActive ? NavBarStyles.selectionActive : NavBarStyles.selection)}
                      >
                        Glossary
                      </NavLink>
                    </li>
                    <li>
                      <a
                        href={process.env.REACT_APP_SWMC_CONTACT_US}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={NavBarStyles.selection}
                      >
                        Contact Us <LaunchIcon className={NavBarStyles.externalIcon} />
                      </a>
                    </li>
                    <li>
                      <a
                        href='https://www.swmc.com/privacy-policy'
                        target='_blank'
                        rel='noopener noreferrer'
                        className={NavBarStyles.selection}
                      >
                        Privacy Policy <LaunchIcon className={NavBarStyles.externalIcon} />
                      </a>
                    </li>
                    <li>
                      <a
                        href='https://www.swmc.com/disclaimer'
                        target='_blank'
                        rel='noopener noreferrer'
                        className={NavBarStyles.selection}
                      >
                        Disclaimers <LaunchIcon className={NavBarStyles.externalIcon} />
                      </a>
                    </li>
                    <li>
                      <a
                        href='https://www.swmc.com/faq'
                        target='_blank'
                        rel='noopener noreferrer'
                        className={NavBarStyles.selection}
                      >
                        FAQ <LaunchIcon className={NavBarStyles.externalIcon} />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </li>
            <li>
              <button
                onClick={handleClick}
                data-to='skipNavigationRef'
                className='keyboard-only'
                ref={skipLoanToolsCategoryLinksRef}
              >
                <div>
                  <div>Return to Navigation</div>
                </div>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </aside>
  );
};

export default LeftNavBar;
